import { faBell, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import mlbLogo from '@sorare/core/src/assets/logos/mlb/mlb.svg';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, DisplayL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import {
  STORAGE,
  useLocalStorage,
} from '@sorare/core/src/hooks/useLocalStorage';
import {
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useMLBEvents } from '@sorare/gameplay/src/lib/events';

import background from './assets/background.jpg';
import players from './assets/players.png';

const Wrapper = styled(Vertical)`
  padding: 0 var(--quadruple-unit);
  padding-top: 3vh;
  min-height: 50vh;
  align-items: center;

  background:
    linear-gradient(to top, var(--current-background-color), transparent 10%),
    linear-gradient(to left, var(--current-background-color), transparent 10%),
    linear-gradient(to bottom, var(--c-nl-300), transparent 30%),
    url(${players}) bottom/auto 50% no-repeat,
    url(${background}) center/cover no-repeat;

  @media ${mediumMobileHeightAndAbove} {
    padding-top: 6vh;
  }

  @media ${tabletAndAbove} {
    padding-top: 10vh;
  }
`;

const Title = styled(HeadlineL)`
  text-transform: uppercase;
  font-family: var(--rigid-square);

  @media ${tabletAndAbove} {
    font: var(--t-display-s);
    font-weight: bold;
    font-family: var(--rigid-square);
  }
`;

const HorizontalWrap = styled(Horizontal)`
  row-gap: 0;
  flex-wrap: wrap;
`;

const LogoWrapper = styled(Horizontal)`
  gap: var(--unit);
  backdrop-filter: blur(10px);
  border-radius: var(--unit);
  background-color: var(--c-nd-200);
  padding: var(--half-unit) var(--unit);
`;

const Numbers = styled(DisplayL)`
  line-height: 1;
  font-family: var(--rigid-square);
  font-size: clamp(48px, 8vw, 200px);
`;

const Caption = styled(BodyS)`
  font-weight: bold;
  color: var(--c-nd-500);
  text-transform: uppercase;

  @media ${tabletAndAbove} {
    font: var(--t-body-l);
    font-weight: bold;
  }
`;

export const OffseasonCountdownTile = () => {
  const track = useMLBEvents();
  const [reminder, setReminder] = useLocalStorage(
    STORAGE.mlbOffseasonCountdownReminder,
    false
  );
  // We need to update the countdown at the same time as the blog post release
  // This will also be useful if for some reason we need to update it
  const {
    flags: { useMlbCountdown = '2025-03-04' },
  } = useFeatureFlags();
  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <Wrapper gap={2}>
      <Vertical>
        <FormattedMessage
          id="OffseasonCountdownTile.hero"
          defaultMessage="<b>{SorareMLB} returns in</b> {date}"
          values={{
            b: (...chunks: ReactNode[]) => (
              <Title>
                <HorizontalWrap center>{chunks}</HorizontalWrap>
              </Title>
            ),
            SorareMLB: (
              <>
                Sorare{' '}
                <LogoWrapper>
                  <img src={mlbLogo} alt="" width={50} />
                  MLB
                </LogoWrapper>
              </>
            ),
            date: (
              <Countdown
                Label={Numbers}
                Caption={Caption}
                date={new Date(useMlbCountdown)}
              />
            ),
          }}
        />
      </Vertical>
      <span>
        <Button
          size={isTabletAndAbove ? 'medium' : 'small'}
          color="secondary"
          onClick={() => {
            setReminder(true);
            track('Click Remind Me Offseason Countdown');
          }}
          disabled={reminder}
        >
          <FontAwesomeIcon icon={reminder ? faCheck : faBell} />
          {reminder ? (
            <FormattedMessage
              id="OffseasonCountdownTile.cta.sub"
              defaultMessage="Subscribed"
            />
          ) : (
            <FormattedMessage
              id="OffseasonCountdownTile.cta.remind"
              defaultMessage="Remind me"
            />
          )}
        </Button>
      </span>
    </Wrapper>
  );
};
